import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  Calendar,
  MapPin,
  Clock,
  ArrowRight,
  Sparkles,
  Users,
  Palette,
  MessageSquare,
  Mail,
  Building2,
  Star,
  ChevronDown,
  ExternalLink,
  X,
  Briefcase,
  TrendingUp,
  Lightbulb
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { EditableText, EditableImage, EditableSection, useAdmin } from './AdminContentEditor';

// Assets
const assets = {
  logoWhite: 'https://customer-assets.emergentagent.com/job_ai-workshop-live/artifacts/oj9sirfv_2.png',
  hero: 'https://customer-assets.emergentagent.com/job_phygital-portal/artifacts/0pjuvgv5_jordan-mcqueen-u9tAl8WR3DI-unsplash.jpg',
  phygitalLogo: 'https://customer-assets.emergentagent.com/job_ai-workshop-live/artifacts/36y9rb0e_download%20%282%29.svg',
  // OG Image - white logo on black background
  ogImage: 'https://customer-assets.emergentagent.com/job_ai-workshop-live/artifacts/oj9sirfv_2.png',
};

// Phygital RSVP URL
const PHYGITAL_URL = 'https://phygital.systems/e/hthf-lafw-2026';

// Color palette
const colors = {
  background: '#FAFAF9',
  backgroundDark: '#0A0A0A',
  black: '#000000',
  white: '#FFFFFF',
};

// Noise overlay
const NoiseOverlay = () => (
  <div 
    className="absolute inset-0 pointer-events-none opacity-[0.03]"
    style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
    }}
  />
);

// VIP Interest Modal
const InterestModal = ({ isOpen, onClose }) => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', interest: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => { 
      onClose(); 
      setSubmitted(false); 
      setFormData({ name: '', email: '', company: '', interest: '' }); 
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" 
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md rounded-2xl p-8 bg-white relative" 
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>

          {!submitted ? (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Submit VIP Interest</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Email address"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Company / Role"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <textarea
                  value={formData.interest}
                  onChange={(e) => setFormData({...formData, interest: e.target.value})}
                  placeholder="Tell us about yourself and your interest"
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                />
                <button type="submit" className="w-full py-3 rounded-lg font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors">
                  Submit
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h3>
              <p className="text-gray-600">We'll be in touch soon.</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Chrome CTA Button
const ChromeCTAButton = ({ children, href, onClick, className = '', secondary = false }) => {
  const content = (
    <motion.span
      className={`relative inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium overflow-hidden ${className}`}
      style={{ 
        background: secondary ? 'transparent' : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 25%, #F5F5F5 50%, #C0C0C0 75%, #E8E8E8 100%)',
        border: secondary ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.5)',
        color: secondary ? '#fff' : '#1a1a1a',
        boxShadow: secondary ? 'none' : '0 0 20px rgba(200,200,200,0.3), inset 0 1px 0 rgba(255,255,255,0.8)',
      }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {!secondary && (
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)' }}
          animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      )}
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </motion.span>
  );

  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer">{content}</a>;
  }
  return <button onClick={onClick}>{content}</button>;
};

// Coming Soon Component for unpublished pages
const ComingSoonPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-black text-white px-4">
    <div className="text-center max-w-md">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
          <Calendar className="w-10 h-10 text-white/60" />
        </div>
        <h1 className="text-4xl font-bold mb-4">Coming Soon</h1>
        <p className="text-white/60 mb-8">
          This page is not yet available. Check back later for updates.
        </p>
        <Link 
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-colors"
        >
          <Home className="w-4 h-4" />
          Back to Home
        </Link>
      </motion.div>
    </div>
  </div>
);

// Main Component
export const HTHFPage = () => {
  // HARDCODED: Page is published but private (accessible via URL, hidden from homepage)
  // This ensures all visitors can see the page content, not "Coming Soon"
  const isPublished = true;
  
  // Check if user is admin (admins can always see the page)
  const { isAdmin } = useAdmin();
  const { isEditMode } = useAdmin();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If not published and not admin, show coming soon
  if (!isPublished && !isAdmin) {
    return <ComingSoonPage />;
  }

  return (
    <div className="min-h-screen relative" style={{ background: colors.background }}>
      <SEO 
        title="High Tech High Fashion 2026"
        description="Where innovation meets elegance. Join us for an exclusive evening during LA Fashion Week 2026."
        image={assets.ogImage}
        url="/hthf"
      />
      <NoiseOverlay />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 border-b border-white/10 overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, #E8E8E8 0%, #C0C0C0 25%, #E8E8E8 50%, #D4D4D4 75%, #E8E8E8 100%)',
            backgroundSize: '200% 100%',
          }}
          animate={{ backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        />
        <div className="relative max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="p-2 rounded-lg hover:bg-black/5 transition-colors">
            <Home className="w-5 h-5 text-gray-700" />
          </Link>
          <div className="flex items-center gap-6">
            <a href="#about" className="text-sm font-medium text-gray-700 hover:text-black transition-colors">The Moment</a>
            <a href="#experience" className="text-sm font-medium text-gray-700 hover:text-black transition-colors">Experience</a>
          </div>
          <EditableImage
            sectionId="hthf-nav"
            field="logo"
            defaultSrc={assets.logoWhite}
            alt="HTHF"
            className="h-10 w-auto object-contain"
            style={{ filter: 'invert(1)' }}
          />
        </div>
      </nav>

      {/* HERO */}
      <EditableSection sectionId="hthf-hero">
        <section className="relative h-screen flex items-center justify-center overflow-hidden pt-12 md:pt-16" style={{ background: colors.black }}>
          {/* Background Image with 55% black overlay */}
          <div className="absolute inset-0">
            <EditableImage
              sectionId="hthf-hero"
              field="background"
              defaultSrc={assets.hero}
              alt=""
              className="w-full h-full"
              objectFit="cover"
            />
            <div className="absolute inset-0 bg-black/55" />
          </div>

          <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Logo */}
              <EditableImage
                sectionId="hthf-hero"
                field="logo"
                defaultSrc={assets.logoWhite}
                alt="High Tech High Fashion"
                className="h-36 sm:h-40 md:h-48 lg:h-56 mx-auto object-contain mb-5 md:mb-8"
              />

              {/* Tagline - Two Lines */}
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/80 max-w-3xl mx-auto mb-1 leading-snug md:leading-relaxed tracking-wide">
                <EditableText
                  sectionId="hthf-hero"
                  field="tagline1"
                  defaultValue="An Elevated Mixer at the Intersection of"
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/80"
                />
              </p>
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/90 max-w-3xl mx-auto mb-5 md:mb-8 leading-snug md:leading-relaxed tracking-wide font-medium">
                <EditableText
                  sectionId="hthf-hero"
                  field="tagline2"
                  defaultValue="Fashion × Culture × Technology"
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/90 font-medium"
                />
              </p>

              {/* Event Details */}
              <div className="flex flex-col items-center justify-center gap-2 md:flex-row md:gap-8 text-white/70 text-sm md:text-lg mb-8 md:mb-12">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                  <EditableText
                    sectionId="hthf-hero"
                    field="date"
                    defaultValue="Date TBD"
                    className="text-white/70 text-sm md:text-lg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 md:w-5 md:h-5" />
                  <EditableText
                    sectionId="hthf-hero"
                    field="time"
                    defaultValue="Time TBD"
                    className="text-white/70 text-sm md:text-lg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5" />
                  <EditableText
                    sectionId="hthf-hero"
                    field="location"
                    defaultValue="Los Angeles"
                    className="text-white/70 text-sm md:text-lg"
                  />
                </div>
              </div>

              {/* Single CTA */}
              <ChromeCTAButton href={PHYGITAL_URL}>
                <Sparkles className="w-5 h-5" />
                <EditableText
                  sectionId="hthf-hero"
                  field="cta_text"
                  defaultValue="Get Your Pass"
                  className="text-black"
                />
              </ChromeCTAButton>
            </motion.div>
          </div>

          {/* Scroll Indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown className="w-5 h-5 text-white/30" />
          </motion.div>
        </section>
      </EditableSection>

      {/* THE MOMENT */}
      <EditableSection sectionId="hthf-about">
        <section id="about" className="py-24 px-4 relative" style={{ background: colors.background }}>
          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-4">
                <EditableText
                  sectionId="hthf-about"
                  field="label"
                  defaultValue="The Moment"
                  className="text-sm uppercase tracking-[0.3em] text-gray-500"
                />
              </p>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-8 leading-tight max-w-3xl mx-auto text-balance">
                <EditableText
                  sectionId="hthf-about"
                  field="headline"
                  defaultValue="High Tech High Fashion is a curated, invitation-forward mixer bringing together the architects of what's next."
                  className="text-3xl md:text-4xl font-light text-gray-900"
                  multiline={true}
                />
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-6 text-balance">
                <EditableText
                  sectionId="hthf-about"
                  field="description1"
                  defaultValue="AI fashion designers, cultural tastemakers, investors, artists, and technologists shaping the future of style and systems."
                  className="text-lg text-gray-600"
                  multiline={true}
                />
              </p>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl mx-auto mb-10 text-balance">
                <EditableText
                  sectionId="hthf-about"
                  field="description2"
                  defaultValue="This is a designed experience where fashion, culture, and technology intersect."
                  className="text-lg text-gray-600"
                  multiline={true}
                />
              </p>
            
            {/* Presented By */}
            <p className="text-sm text-gray-500 mb-4">
              Presented by <span className="font-medium text-gray-700">CodeWater Studio</span> × <span className="font-medium text-gray-700">Data Dreaming</span>
            </p>
          </motion.div>
        </div>
      </section>
      </EditableSection>

      {/* WHAT TO EXPECT */}
      <EditableSection sectionId="hthf-experience">
        <section id="experience" className="py-24 px-4 relative" style={{ background: colors.black }}>
          <div className="max-w-5xl mx-auto relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-light text-white text-center mb-16"
            >
              <EditableText
                sectionId="hthf-experience"
                field="title"
                defaultValue="What to Expect"
                className="text-3xl md:text-4xl font-light text-white"
              />
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Users,
                  title: 'Curated Mixer',
                  description: 'An elevated social experience designed for meaningful connection.',
                  field: 'item1'
                },
                {
                  icon: TrendingUp,
                  title: 'Creative + Capital Exchange',
                  description: 'Designers meet investors. Artists meet platforms. Ideas meet momentum.',
                  field: 'item2'
                },
                {
                  icon: MessageSquare,
                  title: 'Panel Conversation',
                  description: 'A focused dialogue on fashion, culture, and emerging technology.',
                  field: 'item3'
                }
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center p-8 rounded-2xl border border-white/10 hover:border-white/20 transition-colors"
                >
                  <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-10 h-10 text-white/60" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-3">
                    <EditableText
                      sectionId="hthf-experience"
                      field={`${item.field}_title`}
                      defaultValue={item.title}
                      className="text-xl font-medium text-white"
                    />
                  </h3>
                  <p className="text-white/50 text-base leading-relaxed">
                    <EditableText
                      sectionId="hthf-experience"
                      field={`${item.field}_desc`}
                      defaultValue={item.description}
                      className="text-white/50 text-base"
                    />
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </EditableSection>

      {/* AUDIENCE */}
      <EditableSection sectionId="hthf-audience">
        <section className="py-16 md:py-24 px-4 relative" style={{ background: colors.background }}>
          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-4">
                <EditableText
                  sectionId="hthf-audience"
                  field="label"
                  defaultValue="Audience"
                  className="text-sm uppercase tracking-[0.3em] text-gray-500"
                />
              </p>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-8 text-balance max-w-2xl mx-auto">
                <EditableText
                  sectionId="hthf-audience"
                  field="title"
                  defaultValue="Designed for those shaping what's next across culture and technology"
                  className="text-3xl md:text-4xl font-light text-gray-900"
                  multiline={true}
                />
              </h2>
              
              <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {[
                  { text: 'AI and digital fashion designers', field: 'audience1' },
                  { text: 'Investors and venture partners', field: 'audience2' },
                  { text: 'Artists, founders, and creative directors', field: 'audience3' },
                  { text: 'Cultural strategists and tastemakers', field: 'audience4' }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center justify-center gap-3 p-3 rounded-xl bg-gray-50"
                  >
                    <div className="w-2 h-2 rounded-full bg-gray-900 flex-shrink-0" />
                    <span className="text-gray-700">
                      <EditableText
                        sectionId="hthf-audience"
                        field={item.field}
                        defaultValue={item.text}
                        className="text-gray-700"
                      />
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </EditableSection>

      {/* DRESS CODE */}
      <EditableSection sectionId="hthf-dresscode">
        <section className="pt-8 pb-16 md:py-24 px-4 relative" style={{ background: colors.background }}>
          <div className="max-w-xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-4">
                <EditableText
                  sectionId="hthf-dresscode"
                  field="label"
                  defaultValue="Dress Code"
                  className="text-sm uppercase tracking-[0.3em] text-gray-500"
                />
              </p>
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-8 font-mono tracking-wider">
                <EditableText
                  sectionId="hthf-dresscode"
                  field="title"
                  defaultValue="Future.exe"
                  className="text-4xl md:text-5xl font-light text-gray-900 font-mono tracking-wider"
                />
              </h2>
              <div className="space-y-2 text-lg text-gray-600">
                <p><EditableText sectionId="hthf-dresscode" field="line1" defaultValue="Sleek, modern, and expressive." className="text-lg text-gray-600" /></p>
                <p><EditableText sectionId="hthf-dresscode" field="line2" defaultValue="Statement pieces welcome." className="text-lg text-gray-600" /></p>
                <p><EditableText sectionId="hthf-dresscode" field="line3" defaultValue="Dress like you're designing the future." className="text-lg text-gray-600" /></p>
              </div>
            </motion.div>
          </div>
        </section>
      </EditableSection>

      {/* RSVP Section */}
      <EditableSection sectionId="hthf-rsvp">
        <section className="py-24 px-4 relative overflow-hidden" style={{ background: colors.black }}>
          <div className="max-w-2xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4">
                <EditableText sectionId="hthf-rsvp" field="label" defaultValue="RSVP" className="text-sm uppercase tracking-[0.3em] text-white/50" />
              </p>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
                <EditableText sectionId="hthf-rsvp" field="title" defaultValue="Reserve Your Spot" className="text-3xl md:text-4xl font-light text-white" />
              </h2>
              <p className="text-white/70 mb-10 max-w-md mx-auto leading-relaxed text-lg text-balance">
                <EditableText sectionId="hthf-rsvp" field="description" defaultValue="Location details will be shared with confirmed guests. RSVP is required." className="text-white/70 text-lg" />
              </p>

              <div className="mb-8">
                <ChromeCTAButton href={PHYGITAL_URL}>
                  <Sparkles className="w-5 h-5" />
                  <EditableText sectionId="hthf-rsvp" field="cta_text" defaultValue="Get Your Pass" className="text-black" />
                </ChromeCTAButton>
              </div>

              <p className="text-white/40 text-sm">
                <EditableText sectionId="hthf-rsvp" field="subtext" defaultValue="RSVP via Phygital" className="text-white/40 text-sm" />
              </p>
            </motion.div>
          </div>
        </section>
      </EditableSection>

      {/* FOOTER */}
      <EditableSection sectionId="hthf-footer">
        <footer className="py-12 px-4 border-t border-gray-200 pb-24 md:pb-12" style={{ background: colors.background }}>
          <div className="max-w-6xl mx-auto text-center">
            <EditableImage
              sectionId="hthf-footer"
              field="logo"
              defaultSrc={assets.logoWhite}
              alt="HTHF"
              className="h-10 mx-auto object-contain mb-4"
              style={{ filter: 'invert(1)' }}
            />
            <p className="text-gray-600 text-sm mb-2">
              <EditableText sectionId="hthf-footer" field="event_details" defaultValue="TBD · Los Angeles" className="text-gray-600 text-sm" />
            </p>
            <p className="text-gray-400 text-sm">
              <EditableText sectionId="hthf-footer" field="location_note" defaultValue="Location shared with RSVP" className="text-gray-400 text-sm" />
            </p>
          </div>
        </footer>
      </EditableSection>
    </div>
  );
};

export default HTHFPage;
